/**
 * Route builder helpers for generateFeatureRoute
 * Each builder handles one specific case, keeping complexity low.
 */

import { Outlet, Navigate } from 'react-router-dom';
import { DYNAMIC_SEGMENT_MAPPING } from './constants';
import { importPageComponent, hasPageComponent } from './componentLoader';

/**
 * Build a dashboard wrapper route (special case — dashboard page lives at /admin index).
 */
export function buildDashboardRoute(childrenWrappers, fullPath, featureHandle, generateFeatureRoute) {
  const route = {
    path: 'dashboard',
    handle: 'dashboard',
    element: <Outlet />,
    children: [],
  };

  childrenWrappers.forEach(childWrapper => {
    route.children.push(generateFeatureRoute(childWrapper, fullPath, featureHandle));
  });

  return route;
}

/**
 * Build a catch-all leaf route (e.g. knowledge-assets with [[...slug]]).
 */
export function buildCatchAllRoute(featureHandle, componentPath) {
  const CatchAllComponent = importPageComponent(`${componentPath}/[[...slug]]`);
  return {
    path: featureHandle,
    handle: featureHandle,
    element: <Outlet />,
    children: [
      { index: true, element: <CatchAllComponent />, handle: `${featureHandle}-index` },
      { path: '*', element: <CatchAllComponent />, handle: `${featureHandle}-catch-all` },
    ],
  };
}

/**
 * Build a standard CRUD leaf route (list + detail + create).
 */
export function buildLeafRoute(featureHandle, componentPath) {
  const ListComponent = importPageComponent(componentPath);
  const route = {
    path: featureHandle,
    handle: featureHandle,
    element: <Outlet />,
    children: [
      { index: true, element: <ListComponent />, handle: `${featureHandle}-list` },
    ],
  };

  const detailPath = `${componentPath}/[id]`;
  const DetailComponent = hasPageComponent(detailPath)
    ? importPageComponent(detailPath)
    : ListComponent;
  route.children.push({ path: ':id', element: <DetailComponent />, handle: `${featureHandle}-detail` });

  const CreateComponent = importPageComponent(`${componentPath}/add`);
  route.children.push({ path: 'add', element: <CreateComponent />, handle: `${featureHandle}-create` });

  return route;
}

/**
 * Build a parent/group route that wraps children.
 */
export function buildParentRoute(feature, childrenWrappers, componentPath, cleanPath, fullPath, generateFeatureRoute) {
  const route = {
    path: feature.handle,
    handle: feature.handle,
    element: <Outlet />,
    children: [],
  };

  // A parent feature can also have its own page (e.g. "apps", with "nlv"
  // nested under it) — show it as the index route at the parent's own path,
  // alongside its children, rather than as the route element (which would
  // leave no Outlet for the children to render into).
  if (hasPageComponent(componentPath)) {
    const OwnComponent = importPageComponent(componentPath);
    route.children.push({ index: true, element: <OwnComponent />, handle: `${feature.handle}-index` });
  }

  childrenWrappers.forEach(childWrapper => {
    route.children.push(generateFeatureRoute(childWrapper, fullPath, feature.handle));
  });

  applyDynamicSegments(route, feature, childrenWrappers, cleanPath, componentPath);

  return route;
}

/**
 * Add dynamic segment routes / index redirects to a parent route.
 */
function applyDynamicSegments(route, feature, childrenWrappers, cleanPath, componentPath) {
  const segmentName = DYNAMIC_SEGMENT_MAPPING[feature.handle];

  if (segmentName && route.children.length > 0) {
    addDynamicChildren(route, feature, childrenWrappers, cleanPath, segmentName);
  } else if (route.children.length > 0 && !hasPageComponent(componentPath)) {
    prependIndexRedirect(route, childrenWrappers, feature.handle);
  }
}

function addDynamicChildren(route, feature, childrenWrappers, cleanPath, segmentName) {
  const dynamicComponentPath = `${cleanPath}/${feature.handle}/[${segmentName}]`;
  const DynamicComponent = importPageComponent(dynamicComponentPath);

  const staticRoutes = buildStaticRoutes(cleanPath, feature.handle);
  // The parent's own page (buildParentRoute's index child), if it has one —
  // it replaces the redirect to the first child, and must survive the
  // children being rebuilt below.
  const ownIndex = route.children.find((child) => child.index);
  const featureChildren = route.children.filter((child) => !child.index);

  if (feature.handle === 'library') {
    route.children = [...staticRoutes, ...featureChildren];
  } else {
    route.children = [
      ...staticRoutes,
      { path: `:${segmentName}`, element: <DynamicComponent />, handle: `${feature.handle}-dynamic` },
    ];
  }

  if (ownIndex) {
    route.children.unshift(ownIndex);
  } else {
    prependIndexRedirect(route, childrenWrappers, feature.handle);
  }
}

// Static pages that live under a dynamic-segment parent (e.g. taxonomy/tags,
// reports/search-index). Each is registered before the `:segment` route so it
// wins the match, but only when its page.jsx actually exists under that parent.
const STATIC_PAGES = ['tags', 'term-synonym-types', 'search-index'];

function buildStaticRoutes(cleanPath, featureHandle) {
  const staticPages = STATIC_PAGES;
  const routes = [];

  staticPages.forEach(pageName => {
    const staticPath = `${cleanPath}/${featureHandle}/${pageName}`;
    if (hasPageComponent(staticPath)) {
      const StaticComponent = importPageComponent(staticPath);
      routes.push({ path: pageName, element: <StaticComponent />, handle: `${featureHandle}-${pageName}` });
    }
  });

  return routes;
}

function prependIndexRedirect(route, childrenWrappers, featureHandle) {
  const firstChildHandle = childrenWrappers[0]?.node?.handle || childrenWrappers[0]?.handle;
  if (firstChildHandle) {
    route.children.unshift({
      index: true,
      element: <Navigate to={firstChildHandle} replace />,
      handle: `${featureHandle}-index-redirect`,
    });
  }
}
